import { useState, useRef, useEffect, useMemo } from "react";
import { notificationsData, notificationTypeConfig } from "@/mocks/notificationsData";
import type { NotificationType, NotificationItem } from "@/mocks/notificationsData";

type FilterTab = "all" | "unread" | "leads" | "calls" | "system";

const filterTabs: { key: FilterTab; label: string }[] = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
  { key: "leads", label: "Leads" },
  { key: "calls", label: "Calls" },
  { key: "system", label: "System" },
];

// Maps filter tabs to notification types
const filterTypeMap: Record<FilterTab, NotificationType[]> = {
  all: ["lead", "call", "alert", "whatsapp", "assignment", "score", "integration", "system"],
  unread: ["lead", "call", "alert", "whatsapp", "assignment", "score", "integration", "system"],
  leads: ["lead", "alert", "score", "assignment"],
  calls: ["call", "whatsapp"],
  system: ["system", "integration", "assignment", "score"],
};

export default function NotificationDropdown() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<NotificationItem[]>(notificationsData);
  const [activeTab, setActiveTab] = useState<FilterTab>("all");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLButtonElement>(null);

  const unreadCount = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        bellRef.current &&
        !bellRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const filtered = useMemo(() => {
    let result = notifications;
    if (activeTab === "unread") {
      result = result.filter((n) => !n.read);
    } else if (activeTab !== "all") {
      const allowed = filterTypeMap[activeTab];
      result = result.filter((n) => allowed.includes(n.type));
    }
    return result;
  }, [notifications, activeTab]);

  const handleMarkAllRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const handleNotificationClick = (id: number) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  };

  return (
    <div className="relative">
      {/* Bell button */}
      <button
        ref={bellRef}
        onClick={() => setOpen((prev) => !prev)}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
      >
        <i className="ri-notification-3-line text-xl text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center leading-none">
            {unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <>
          {/* Invisible backdrop for closing on outside click (mobile-friendly) */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />

          <div
            ref={dropdownRef}
            className="absolute right-0 top-full mt-2 w-[380px] bg-white rounded-xl border border-gray-200 shadow-lg z-50 overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
                {unreadCount > 0 && (
                  <span className="text-xs text-indigo-600 font-medium">{unreadCount} unread</span>
                )}
              </div>
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-indigo-600 hover:text-indigo-700 font-medium transition-colors whitespace-nowrap"
              >
                Mark all read
              </button>
            </div>

            {/* Filter tabs */}
            <div className="flex items-center gap-1 px-4 py-2 border-b border-gray-100">
              {filterTabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors whitespace-nowrap ${
                    activeTab === tab.key
                      ? "bg-indigo-50 text-indigo-600"
                      : "text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Notification list */}
            <div className="max-h-96 overflow-y-auto">
              {filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-indigo-50 flex items-center justify-center mb-4">
                    <i className="ri-notification-3-line text-3xl text-indigo-300" />
                  </div>
                  <p className="text-base font-semibold text-gray-700 mb-1">You&apos;re all caught up!</p>
                  <p className="text-sm text-gray-500">No new notifications right now</p>
                </div>
              ) : (
                filtered.map((item) => {
                  const config = notificationTypeConfig[item.type];
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleNotificationClick(item.id)}
                      className={`w-full flex items-start gap-3 px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors text-left ${
                        !item.read ? "bg-indigo-50/30" : "bg-white"
                      }`}
                    >
                      {/* Left colored dot for unread + icon */}
                      <div className="relative flex-shrink-0">
                        {!item.read && (
                          <span className="absolute -left-2 top-1/2 -translate-y-1/2 w-[3px] h-8 bg-indigo-500 rounded-full" />
                        )}
                        <div className={`w-9 h-9 rounded-full ${config.bg} flex items-center justify-center`}>
                          <i className={`${config.icon} ${config.color} text-base`} />
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{item.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{item.description}</p>
                        <p className="text-[11px] text-gray-400 mt-1">{item.time}</p>
                      </div>

                      {/* Unread dot */}
                      {!item.read && (
                        <div className="flex-shrink-0 mt-1.5">
                          <span className="block w-1.5 h-1.5 rounded-full bg-indigo-500" />
                        </div>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}